// The upgrade pool, the effect pipeline, and the draft.
//
// Three problems are solved in this file, and they are solved together
// because solving them separately is what makes roguelites boring.
//
// ── 1. How big is the pool? ───────────────────────────────────────────
//
// The number that governs whether runs feel samey is not N, the pool
// size — it is N/k, pool size over picks-per-run. A run that takes 4 of
// 28 has 20,475 unordered hands; a run that takes 4 of 8 has 70, and
// you have seen all of them by your tenth attempt. Slay the Spire lands
// around 15, Balatro around 30, Vampire Survivors around 3.3 — and
// Vampire Survivors is the one people call repetitive. N=28, k=4 puts
// us at 7, comfortably inside the good band without asking a visitor to
// read 60 cards.
//
// ── 2. Why one pipeline instead of 28 special cases? ──────────────────
//
// This is the Balatro trick, and it is the only structural idea in this
// file that matters. If every upgrade reaches into the sim and does its
// own private thing, then N upgrades give you N behaviours. If instead
// every upgrade is a *modifier on a shared resolution pipeline*, then
// any two upgrades that touch the same hook interact automatically, and
// N upgrades give you something closer to N² — for free, with no
// combination written by hand and none to forget to test.
//
// So there is exactly one way an upgrade affects the game: it declares
// `mods`, a list of `{hook, op, value, when}` records, and the sim asks
// `applyModifiers(held, hook, base, ctx)` at each of the sixteen hook
// points. A handful of upgrades additionally declare `rules` — named
// boolean rule changes for the things a number cannot express, like
// "SIGSTOP also reaps every zombie" — and even those are read through
// one accessor, `hasRule`, rather than by pattern-matching on ids.
//
// The `holds` condition is where this pays off most literally: an
// upgrade can be stronger *because you also hold another one*. Synergy
// stops being flavour text and becomes arithmetic.
//
// ── 3. Why does resolution order not matter? ──────────────────────────
//
// Because ops resolve by class, never by list position: every matching
// `add` is summed, then every matching `mul` is multiplied, then floors
// and caps clamp. Sum, product, max and min are all commutative, so
// `applyModifiers(["a","b"], ...)` and `applyModifiers(["b","a"], ...)`
// are the same number by construction. There is deliberately no `set`
// op — it is the one operation that would make draft order matter, and
// a run whose outcome depends on which of two cards you were shown
// first is a run you cannot reason about.
//
// Pure and data-driven throughout: no React, no DOM, no `Math.random`,
// no `Date.now`. Every random choice comes from the seeded `Rng`, so a
// draft is as replayable as the fight around it.

import type { Rng } from "./rng";
import type { SignalId, Upgrade, UpgradeArchetype, UpgradeRarity } from "./types";

// ── Shape of the run ──────────────────────────────────────────────────

/** Total upgrades in the pool. */
export const POOL_SIZE = 28;

/** Drafts per run — one after each of phases 1-4. The fifth phase is
 *  the last one, so there is nothing to spend a pick on afterwards. */
export const PICKS_PER_RUN = 4;

/** Cards on the draft screen. Three is the number at which a choice is
 *  a decision rather than a menu. */
export const OFFERS_PER_DRAFT = 3;

/** The intended rarity split, declared so the tests can hold the pool
 *  to it rather than to whatever it happens to contain. */
export const RARITY_SPLIT: Record<UpgradeRarity, number> = {
  common: 16,
  uncommon: 9,
  rare: 3,
};

// ── Hooks ─────────────────────────────────────────────────────────────

/**
 * The sixteen places the sim consults the upgrade pipeline. Every one
 * of them is a *number the sim was about to use anyway*, handed to
 * `applyModifiers` first. That is the whole integration surface: no
 * upgrade ever calls into the sim, and the sim never asks which
 * upgrades are held.
 */
export type Hook =
  /** Damage about to be applied to DAEMON. `ctx.signal` says by what. */
  | "damage-dealt"
  /** Damage about to be applied to the player by a bullet. */
  | "damage-taken"
  /** PRIV awarded for one graze. */
  | "graze-priv"
  /** GRAZE_R, in logical units, before the proximity test. */
  | "graze-radius"
  /** The player's PRIV ceiling. */
  | "priv-max"
  /** PRIV a signal costs to fire. `ctx.signal` says which. */
  | "signal-cost"
  /** Cooldown in ticks after a signal fires. `ctx.signal` says which. */
  | "signal-cooldown"
  /** Magnitude of a signal's effect — SIGSTOP freeze ticks, renice
   *  duration. Damage goes through `damage-dealt`, not this. */
  | "signal-power"
  /** Speed of a bullet at spawn, in logical units per tick. */
  | "bullet-speed"
  /** Bullets in one emission step. Rounded by the caller. */
  | "bullet-count"
  /** Invulnerability ticks granted by a hit. */
  | "iframes"
  /** Telegraph lead in ticks. Shortening this is a real cost. */
  | "telegraph"
  /** Target arena scale for the runlevel-2 shrink device, 0-1. */
  | "arena-scale"
  /** HP restored when a phase is cleared. Base is 0 — this hook exists
   *  entirely so that healing upgrades have somewhere to live. */
  | "phase-heal"
  /** Multiplier on how fast DAEMON forks children. */
  | "fork-rate"
  /** Ticks the cursor must dwell to reap a zombie. */
  | "reap-ticks";

// ── Rules ─────────────────────────────────────────────────────────────

/**
 * Rule changes — the things a multiplier cannot say. Kept to four on
 * purpose: every one of these is a branch the sim has to carry
 * forever, whereas a modifier is free. Rares get the loud ones.
 */
export type Rule =
  /** `sigqueue(3)` — every Nth SIGTERM lands as a SIGKILL. */
  | "sigterm-fifth-kills"
  /** `wait(2)` — SIGSTOP also reaps every zombie on the field. */
  | "sigstop-reaps"
  /** `sync; sync; sync` — a hit halves PRIV instead of zeroing it. */
  | "priv-survives-hit"
  /** `LD_PRELOAD` — SIGTERM connects through a handler window. */
  | "sigterm-pierces-guard";

/** How often `sigqueue(3)` promotes a SIGTERM. Every fifth. */
export const SIGQUEUE_PERIOD = 5;

// ── Conditions ────────────────────────────────────────────────────────

/** Numbers a condition can read out of the context. All derived, so the
 *  sim passes raw state and never has to precompute anything. */
export type ModField =
  | "hpFrac"
  | "privFrac"
  | "priv"
  | "bullets"
  | "children"
  | "zombies"
  | "phaseIndex"
  | "sigtermCount"
  | "hitsTaken"
  | "arenaScale";

/**
 * When a modifier applies. Data, not a closure — so the pool stays
 * serialisable, diffable and printable on the draft screen, and so a
 * condition can never smuggle in a clock or a `Math.random`.
 */
export type Cond =
  | { k: "signal"; id: SignalId }
  /** True when the player also holds `id`. This is what turns a synergy
   *  link from a weighting hint into a mechanic. */
  | { k: "holds"; id: string }
  | { k: "lt"; field: ModField; value: number }
  | { k: "gte"; field: ModField; value: number }
  | { k: "all"; of: Cond[] };

/** Everything a condition might want to know. All optional: a caller
 *  that hasn't got a number simply doesn't pass it, and conditions that
 *  depend on it are treated as unmet rather than crashing. */
export type ModContext = {
  signal?: SignalId;
  hp?: number;
  hpMax?: number;
  priv?: number;
  privMax?: number;
  /** Live projectiles on the field. */
  bullets?: number;
  children?: number;
  zombies?: number;
  phaseIndex?: number;
  /** SIGTERMs fired so far this run — drives `sigqueue(3)`. */
  sigtermCount?: number;
  hitsTaken?: number;
  arenaScale?: number;
};

const EMPTY_CTX: ModContext = {};

/** Read a field, or `undefined` if the caller didn't supply enough to
 *  compute it. `undefined` fails every comparison, which is the safe
 *  direction: an upgrade is inert rather than wrongly active. */
function field(f: ModField, ctx: ModContext): number | undefined {
  switch (f) {
    case "hpFrac":
      return ctx.hp !== undefined && ctx.hpMax ? ctx.hp / ctx.hpMax : undefined;
    case "privFrac":
      return ctx.priv !== undefined && ctx.privMax ? ctx.priv / ctx.privMax : undefined;
    case "priv":
      return ctx.priv;
    case "bullets":
      return ctx.bullets;
    case "children":
      return ctx.children;
    case "zombies":
      return ctx.zombies;
    case "phaseIndex":
      return ctx.phaseIndex;
    case "sigtermCount":
      return ctx.sigtermCount;
    case "hitsTaken":
      return ctx.hitsTaken;
    case "arenaScale":
      return ctx.arenaScale;
  }
}

// `held` is at most PICKS_PER_RUN long, so a linear scan beats building
// a Set — this runs inside the 60Hz loop and allocating per tick is how
// you get a sawtooth GC trace in a bullet hell.
function condHolds(c: Cond, held: readonly string[], ctx: ModContext): boolean {
  switch (c.k) {
    case "signal":
      return ctx.signal === c.id;
    case "holds":
      return held.includes(c.id);
    case "lt": {
      const v = field(c.field, ctx);
      return v !== undefined && v < c.value;
    }
    case "gte": {
      const v = field(c.field, ctx);
      return v !== undefined && v >= c.value;
    }
    case "all":
      return c.of.every((x) => condHolds(x, held, ctx));
  }
}

// ── Modifiers ─────────────────────────────────────────────────────────

/**
 * `add` and `mul` are the workhorses; `floor` and `cap` exist so an
 * upgrade can say "this never goes below/above X" without needing a
 * `set` op. There is no `set`, deliberately — see the header.
 */
export type ModOp = "add" | "mul" | "floor" | "cap";

export type Modifier = {
  hook: Hook;
  op: ModOp;
  value: number;
  /** Omitted means unconditional. */
  when?: Cond;
};

/**
 * Run `value` through every modifier the held set contributes to `hook`.
 *
 * Resolution is by op class, never by list position:
 *
 *   1. sum of all matching `add`
 *   2. product of all matching `mul`
 *   3. the strongest `floor`, then the strongest `cap`
 *
 * which makes the result independent of the order of `held` — a
 * property the tests assert, because it is easy to lose and expensive
 * to notice you have lost.
 */
export function applyModifiers(
  held: readonly string[],
  hook: Hook,
  value: number,
  ctx: ModContext = EMPTY_CTX,
): number {
  const entries = MODS_BY_HOOK.get(hook);
  if (!entries || held.length === 0) return value;

  let sum = 0;
  let product = 1;
  let lo: number | undefined;
  let hi: number | undefined;

  // The index is built in pool order, so the traversal is fixed by the
  // source file rather than by the order cards were drafted.
  for (const e of entries) {
    if (!held.includes(e.id)) continue;
    const m = e.mod;
    if (m.when && !condHolds(m.when, held, ctx)) continue;
    switch (m.op) {
      case "add":
        sum += m.value;
        break;
      case "mul":
        product *= m.value;
        break;
      case "floor":
        lo = lo === undefined ? m.value : Math.max(lo, m.value);
        break;
      case "cap":
        hi = hi === undefined ? m.value : Math.min(hi, m.value);
        break;
    }
  }

  let out = (value + sum) * product;
  if (lo !== undefined) out = Math.max(out, lo);
  if (hi !== undefined) out = Math.min(out, hi);
  return out;
}

/** `applyModifiers` for a hook whose value must be a whole number of
 *  ticks or bullets. Rounds once, at the end, so intermediate factors
 *  don't each lose a fraction. */
export function applyModifiersInt(
  held: readonly string[],
  hook: Hook,
  value: number,
  ctx?: ModContext,
): number {
  return Math.round(applyModifiers(held, hook, value, ctx));
}

// ── Rule accessors ────────────────────────────────────────────────────

/** Does the held set turn on `rule`? The only way to ask — nothing in
 *  the sim should ever compare against an upgrade id directly. */
export function hasRule(held: readonly string[], rule: Rule): boolean {
  const owners = RULE_OWNERS.get(rule);
  if (!owners) return false;
  return owners.some((id) => held.includes(id));
}

/** Every rule currently in force, in pool order. For the HUD, and for
 *  debugging a run that is behaving strangely. */
export function activeRules(held: readonly string[]): Rule[] {
  const out: Rule[] = [];
  for (const u of UPGRADES) {
    if (!held.includes(u.id)) continue;
    for (const r of u.rules) if (!out.includes(r)) out.push(r);
  }
  return out;
}

/**
 * Does *this* SIGTERM land as a SIGKILL? `sigqueue(3)` queues your
 * polite requests and delivers every fifth one with force.
 *
 * `count` is the number of SIGTERMs fired before this one, so the first
 * promotion is the fifth press, not the first.
 */
export function sigtermPromotes(held: readonly string[], count: number): boolean {
  if (!hasRule(held, "sigterm-fifth-kills")) return false;
  return (count + 1) % SIGQUEUE_PERIOD === 0;
}

/** PRIV left after taking a hit. Normally none — courage is not
 *  bankable through a hit — unless something flushed it first. */
export function privAfterHit(held: readonly string[], priv: number): number {
  return hasRule(held, "priv-survives-hit") ? Math.floor(priv / 2) : 0;
}

// ── The pool ──────────────────────────────────────────────────────────

export type UpgradeDef = Upgrade & {
  mods: Modifier[];
  rules: Rule[];
};

// Terse builders. The pool below is a table, and a table you can read
// down a column is worth the six lines it costs to get one.
const add = (hook: Hook, value: number, when?: Cond): Modifier =>
  when ? { hook, op: "add", value, when } : { hook, op: "add", value };
const mul = (hook: Hook, value: number, when?: Cond): Modifier =>
  when ? { hook, op: "mul", value, when } : { hook, op: "mul", value };
const atLeast = (hook: Hook, value: number): Modifier => ({ hook, op: "floor", value });
const atMost = (hook: Hook, value: number, when?: Cond): Modifier =>
  when ? { hook, op: "cap", value, when } : { hook, op: "cap", value };

const onSig = (id: SignalId): Cond => ({ k: "signal", id });
const holds = (id: string): Cond => ({ k: "holds", id });
const gte = (f: ModField, value: number): Cond => ({ k: "gte", field: f, value });
const lt = (f: ModField, value: number): Cond => ({ k: "lt", field: f, value });

/**
 * Twenty-eight upgrades, five archetypes, 16/9/3 common/uncommon/rare.
 *
 * The rarity rule is a design rule, not a power rule: **commons change
 * a number, rares change a rule.** A common that reads "+15% damage" is
 * doing its job. A rare that reads "+45% damage" is not a rare, it is a
 * common with a bigger font — a rare has to make you reconsider what
 * the rest of your run is for.
 *
 * Names are Unix and the audience already knows what these commands do,
 * which is what makes the joke land *and* does the tutorialising for
 * free: nobody needs told what `swapoff -a` costs you.
 */
export const UPGRADES: UpgradeDef[] = [
  // ── AGGRESSION ── kill it faster, at whatever price ─────────────────
  {
    id: "kill-9",
    name: "kill -9",
    archetype: "aggression",
    rarity: "common",
    blurb:
      "The only argument that has never needed a second opinion. SIGKILL hits 30% harder — 50% if your polite requests are queueing.",
    synergy: ["sigqueue", "chrt-99"],
    mods: [
      mul("damage-dealt", 1.3, onSig("sigkill")),
      mul("damage-dealt", 1.15, { k: "all", of: [onSig("sigkill"), holds("sigqueue")] }),
    ],
    rules: [],
  },
  {
    id: "renice-self",
    name: "nice -n -20",
    archetype: "aggression",
    rarity: "common",
    blurb: "Nobody said only DAEMON gets to renice itself. SIGTERM does +3 damage.",
    synergy: ["sigqueue", "ld-preload"],
    mods: [add("damage-dealt", 3, onSig("sigterm"))],
    rules: [],
  },
  {
    id: "o3",
    name: "-O3",
    archetype: "aggression",
    rarity: "common",
    blurb:
      "Unsafe optimisations enabled globally. Everything you fire lands 15% harder. The compiler did warn you.",
    synergy: ["ffast-math", "kill-9"],
    mods: [mul("damage-dealt", 1.15)],
    rules: [],
  },
  {
    id: "ld-preload",
    name: "LD_PRELOAD",
    archetype: "aggression",
    rarity: "uncommon",
    blurb:
      "Your shim loads before its handlers do. SIGTERM connects even inside a handler window.",
    synergy: ["renice-self", "sigqueue", "strace-f"],
    mods: [],
    rules: ["sigterm-pierces-guard"],
  },
  {
    id: "setuid",
    name: "setuid",
    archetype: "aggression",
    rarity: "uncommon",
    blurb:
      "Escalation is a privilege, and privilege is a resource. Above 80% PRIV, everything you fire hits 40% harder.",
    synergy: ["ulimit-c", "umask-000", "swapoff"],
    mods: [mul("damage-dealt", 1.4, gte("privFrac", 0.8))],
    rules: [],
  },
  {
    id: "sigqueue",
    name: "sigqueue(3)",
    archetype: "aggression",
    rarity: "rare",
    blurb:
      "Your polite requests do not get dropped, they get queued. Every fifth SIGTERM arrives as a SIGKILL.",
    synergy: ["kill-9", "renice-self", "renice-19"],
    mods: [],
    rules: ["sigterm-fifth-kills"],
  },

  // ── CONTROL ── tempo, cooldowns, deciding when things happen ────────
  {
    id: "renice-19",
    name: "renice +19",
    archetype: "control",
    rarity: "common",
    blurb: "Everything you own moves to a better scheduling class. All signal cooldowns down 15%.",
    synergy: ["chrt-99", "sigqueue"],
    mods: [mul("signal-cooldown", 0.85)],
    rules: [],
  },
  {
    id: "sigcont-eventually",
    name: "SIGCONT, eventually",
    archetype: "control",
    rarity: "common",
    blurb: "SIGSTOP holds 35% longer. You get to decide what 'eventually' means.",
    synergy: ["wait2", "chrt-99"],
    mods: [mul("signal-power", 1.35, onSig("sigstop"))],
    rules: [],
  },
  {
    id: "strace-f",
    name: "strace -f",
    archetype: "control",
    rarity: "common",
    blurb: "Follow forks. strace costs nothing and comes back nearly twice as often.",
    synergy: ["ld-preload", "cpuset"],
    mods: [
      atMost("signal-cost", 0, onSig("strace")),
      mul("signal-cooldown", 0.6, onSig("strace")),
    ],
    rules: [],
  },
  {
    id: "cpuset",
    name: "cpuset",
    archetype: "control",
    rarity: "uncommon",
    blurb: "Pin the danger to a core you are watching. Telegraphs last 20% longer.",
    synergy: ["strace-f", "nohup", "ffast-math"],
    mods: [mul("telegraph", 1.2)],
    rules: [],
  },
  {
    id: "chrt-99",
    name: "chrt -f 99",
    archetype: "control",
    rarity: "uncommon",
    blurb: "Real-time priority, granted to yourself. Every signal costs 1 less PRIV.",
    synergy: ["renice-19", "kill-9", "sigcont-eventually"],
    mods: [add("signal-cost", -1), atLeast("signal-cost", 0)],
    rules: [],
  },
  {
    id: "wait2",
    name: "wait(2)",
    archetype: "control",
    rarity: "rare",
    blurb:
      "Freeze the field and collect what is owed. SIGSTOP now reaps every zombie on the arena, instantly, from anywhere.",
    synergy: ["sigcont-eventually", "fsck-y", "ulimit-u"],
    mods: [],
    rules: ["sigstop-reaps"],
  },

  // ── GREED ── graze harder, bank more PRIV, deserve what follows ─────
  {
    id: "ulimit-c",
    name: "ulimit -c unlimited",
    archetype: "greed",
    rarity: "common",
    blurb: "Core dumps unlimited. +4 max PRIV, and everything you lose is at least recorded.",
    synergy: ["setuid", "swapoff"],
    mods: [add("priv-max", 4)],
    rules: [],
  },
  {
    id: "umask-000",
    name: "umask 000",
    archetype: "greed",
    rarity: "common",
    blurb:
      "Nothing on this system is protected, least of all you. +1 PRIV per graze, +2 if you are already splitting the stream.",
    synergy: ["tee", "vmstat"],
    mods: [add("graze-priv", 1), add("graze-priv", 1, holds("tee"))],
    rules: [],
  },
  {
    id: "tee",
    name: "tee",
    archetype: "greed",
    rarity: "common",
    blurb: "Split the stream and read both halves. Graze range up 25%.",
    synergy: ["umask-000", "vmstat"],
    mods: [mul("graze-radius", 1.25)],
    rules: [],
  },
  {
    id: "vmstat",
    name: "vmstat 1",
    archetype: "greed",
    rarity: "common",
    blurb: "Under load, courage pays better. +60% PRIV per graze while 40 or more projectiles are live.",
    synergy: ["urandom", "ulimit-u", "tee"],
    mods: [mul("graze-priv", 1.6, gte("bullets", 40))],
    rules: [],
  },
  {
    id: "swapoff",
    name: "swapoff -a",
    archetype: "greed",
    rarity: "uncommon",
    blurb: "No paging out, no second chances. +8 max PRIV, and every hit costs 20% more.",
    synergy: ["setuid", "ulimit-c", "sync-sync-sync"],
    mods: [add("priv-max", 8), mul("damage-taken", 1.2)],
    rules: [],
  },
  {
    id: "sync-sync-sync",
    name: "sync; sync; sync",
    archetype: "greed",
    rarity: "uncommon",
    blurb:
      "Flush before the crash, the way your elders taught you. A hit now keeps half your PRIV instead of all of it.",
    synergy: ["swapoff", "ulimit-c", "oom-score-adj"],
    mods: [],
    rules: ["priv-survives-hit"],
  },

  // ── SURVIVAL ── still being here is a strategy ──────────────────────
  {
    id: "nohup",
    name: "nohup",
    archetype: "survival",
    rarity: "common",
    blurb: "Hang up all you like. Invulnerability after a hit lasts 40% longer.",
    synergy: ["cgroup-high", "cpuset"],
    mods: [mul("iframes", 1.4)],
    rules: [],
  },
  {
    id: "fsck-y",
    name: "fsck -y",
    archetype: "survival",
    rarity: "common",
    blurb:
      "Repair on mount, answer yes to everything. +18 HP when a phase clears, and zombies reap 30% faster.",
    synergy: ["wait2", "cgroup-high"],
    mods: [add("phase-heal", 18), mul("reap-ticks", 0.7)],
    rules: [],
  },
  {
    id: "cgroup-high",
    name: "cgroup memory.high",
    archetype: "survival",
    rarity: "common",
    blurb:
      "Cap the blast radius before it is a blast. Damage taken down 15%, and down another 15% behind your own root.",
    synergy: ["chroot", "nohup", "fsck-y"],
    mods: [mul("damage-taken", 0.85), mul("damage-taken", 0.85, holds("chroot"))],
    rules: [],
  },
  {
    id: "chroot",
    name: "chroot",
    archetype: "survival",
    rarity: "uncommon",
    blurb: "Your own root, your own walls. The arena stops 12% short of where it was closing to.",
    synergy: ["cgroup-high", "ulimit-u"],
    mods: [add("arena-scale", 0.12)],
    rules: [],
  },
  {
    id: "oom-score-adj",
    name: "oom_score_adj -1000",
    archetype: "survival",
    rarity: "uncommon",
    blurb: "The reaper is instructed to skip you. Below a third HP, incoming damage is cut 40%.",
    synergy: ["cgroup-high", "sync-sync-sync", "nohup"],
    mods: [mul("damage-taken", 0.6, lt("hpFrac", 0.34))],
    rules: [],
  },

  // ── CHAOS ── every one of these is a trade, and you will take it ────
  {
    id: "urandom",
    name: "/dev/urandom",
    archetype: "chaos",
    rarity: "common",
    blurb:
      "Entropy, distributed fairly. Projectiles move 12% slower and there is always one more of them.",
    synergy: ["vmstat", "ulimit-u"],
    mods: [mul("bullet-speed", 0.88), add("bullet-count", 1)],
    rules: [],
  },
  {
    id: "pipe",
    name: "pipe(2)",
    archetype: "chaos",
    rarity: "common",
    blurb: "Everything connects to everything, including the parts of you. +1 PRIV per graze, +2 damage per hit taken.",
    synergy: ["umask-000", "tee", "sync-sync-sync"],
    mods: [add("graze-priv", 1), add("damage-taken", 2)],
    rules: [],
  },
  {
    id: "sigrtmin",
    name: "SIGRTMIN",
    archetype: "chaos",
    rarity: "common",
    blurb: "Real-time signals, real-time regrets. Cooldowns down 30%, every signal costs 1 more PRIV.",
    synergy: ["chrt-99", "renice-19", "ulimit-c"],
    mods: [mul("signal-cooldown", 0.7), add("signal-cost", 1)],
    rules: [],
  },
  {
    id: "ffast-math",
    name: "-ffast-math",
    archetype: "chaos",
    rarity: "uncommon",
    blurb:
      "Correctness is a spectrum and you are choosing a point on it. Telegraphs 25% shorter, damage dealt up 30%.",
    synergy: ["o3", "cpuset"],
    mods: [mul("telegraph", 0.75), mul("damage-dealt", 1.3)],
    rules: [],
  },
  {
    id: "ulimit-u",
    name: "ulimit -u unlimited",
    archetype: "chaos",
    rarity: "rare",
    blurb:
      "The process cap is gone and so is the swap thrash. The arena never shrinks again — and DAEMON forks twice as fast, forever.",
    synergy: ["wait2", "urandom", "vmstat"],
    mods: [atLeast("arena-scale", 1), mul("fork-rate", 2)],
    rules: [],
  },
];

// ── Indexes ───────────────────────────────────────────────────────────
//
// Built once at module load. `applyModifiers` and `hasRule` are called
// from inside the 60Hz loop, and neither should be walking 28 records
// and 40 modifiers to discover that the player holds four things.

const BY_ID: ReadonlyMap<string, UpgradeDef> = new Map(UPGRADES.map((u) => [u.id, u]));

const MODS_BY_HOOK: ReadonlyMap<Hook, { id: string; mod: Modifier }[]> = (() => {
  const m = new Map<Hook, { id: string; mod: Modifier }[]>();
  for (const u of UPGRADES) {
    for (const mod of u.mods) {
      const list = m.get(mod.hook);
      if (list) list.push({ id: u.id, mod });
      else m.set(mod.hook, [{ id: u.id, mod }]);
    }
  }
  return m;
})();

const RULE_OWNERS: ReadonlyMap<Rule, string[]> = (() => {
  const m = new Map<Rule, string[]>();
  for (const u of UPGRADES) {
    for (const r of u.rules) {
      const list = m.get(r);
      if (list) list.push(u.id);
      else m.set(r, [u.id]);
    }
  }
  return m;
})();

export const getUpgrade = (id: string): UpgradeDef | undefined => BY_ID.get(id);

/** Ids, in pool order. Handy for the tests and the debug overlay. */
export const UPGRADE_IDS: string[] = UPGRADES.map((u) => u.id);

// ── The draft ─────────────────────────────────────────────────────────

/**
 * Rarity weights. The pool is 16/9/3, so with these weights a single
 * offer slot is roughly 76% common, 21% uncommon, 3% rare — which
 * across four drafts of three means a bit under a third of runs see a
 * rare at all. That is the number to tune if rares feel like a myth or
 * feel routine; nothing else in the draft is as sensitive.
 */
const RARITY_WEIGHT: Record<UpgradeRarity, number> = {
  common: 10,
  uncommon: 5,
  rare: 2,
};

/** Weight multiplier per synergy link with something already held. */
const SYNERGY_BONUS = 0.9;
/** Ceiling on the synergy multiplier, so a wide build cannot collapse
 *  the draft into a single inevitable card. A choose-one-of-three where
 *  one option is obviously correct is not a choice. */
const SYNERGY_CAP = 3;

/** Weight multiplier per held card of the same archetype. */
const ARCHETYPE_BONUS = 0.18;
const ARCHETYPE_CAP = 1.7;

/**
 * How strongly an offer is pulled toward the build in progress.
 *
 * Some pull is essential: four unrelated buffs is a shopping list,
 * whereas four cards that argue with each other is a run you can
 * describe afterwards in one sentence. Too much pull and the second
 * draft decides the other three.
 */
function weightFor(u: UpgradeDef, held: ReadonlySet<string>): number {
  let links = 0;
  let sameArch = 0;
  for (const id of held) {
    const h = BY_ID.get(id);
    if (!h) continue;
    // Counted in both directions, so a link only has to be authored on
    // one side to be felt from the other.
    if (u.synergy.includes(id)) links += 1;
    if (h.synergy.includes(u.id)) links += 1;
    if (h.archetype === u.archetype) sameArch += 1;
  }
  const synergy = Math.min(SYNERGY_CAP, 1 + SYNERGY_BONUS * links);
  const archetype = Math.min(ARCHETYPE_CAP, 1 + ARCHETYPE_BONUS * sameArch);
  return RARITY_WEIGHT[u.rarity] * synergy * archetype;
}

/** One weighted draw. Deterministic: exactly one `rng()` call, and the
 *  walk is over the array in the order it was given. */
function drawOne(pool: UpgradeDef[], held: ReadonlySet<string>, rng: Rng): number {
  let total = 0;
  const weights = pool.map((u) => {
    const w = weightFor(u, held);
    total += w;
    return w;
  });
  let r = rng() * total;
  for (let i = 0; i < pool.length; i++) {
    r -= weights[i]!;
    if (r <= 0) return i;
  }
  // Only reachable through float slop on the very last entry.
  return pool.length - 1;
}

/**
 * Roll the offers for one draft.
 *
 * Guarantees, all of which the tests hold it to:
 *
 *   - never offers something already held;
 *   - never offers the same thing twice;
 *   - returns `count` offers, or every remaining candidate if the pool
 *     has run dry — it degrades rather than looping;
 *   - weighted toward synergy with the build, so all three stay
 *     plausible picks rather than one obvious one and two filler;
 *   - weighted by rarity, with rares genuinely rare;
 *   - never all one archetype, so the choice has a shape;
 *   - fully determined by `(held, rng)`, because a seeded run whose
 *     draft is not seeded is not a seeded run.
 *
 * Note there is no rejection sampling anywhere: candidates are removed
 * from the pool as they are drawn, so the loop runs exactly `count`
 * times whatever the held set looks like.
 */
export function rollOffers(
  held: readonly string[],
  rng: Rng,
  count: number = OFFERS_PER_DRAFT,
): string[] {
  const heldSet = new Set(held);
  const pool = UPGRADES.filter((u) => !heldSet.has(u.id));
  const want = Math.min(count, pool.length);
  if (want <= 0) return [];

  const chosen: UpgradeDef[] = [];
  for (let i = 0; i < want; i++) {
    const idx = drawOne(pool, heldSet, rng);
    chosen.push(pool[idx]!);
    pool.splice(idx, 1);
  }

  // Diversity backstop. Three cards from one archetype is a menu, not a
  // decision — you already know what kind of run you are having. Swap
  // the last one for something from elsewhere, still weighted, so the
  // replacement is a real offer rather than a random consolation.
  if (chosen.length >= 2 && pool.length > 0) {
    const arch = chosen[0]!.archetype;
    if (chosen.every((u) => u.archetype === arch)) {
      const others = pool.filter((u) => u.archetype !== arch);
      if (others.length > 0) {
        chosen[chosen.length - 1] = others[drawOne(others, heldSet, rng)]!;
      }
    }
  }

  return chosen.map((u) => u.id);
}

/** Offers grouped for the draft screen: the full record, not just ids,
 *  so the renderer never has to look anything up. */
export function describeOffers(ids: readonly string[]): UpgradeDef[] {
  return ids.map((id) => BY_ID.get(id)).filter((u): u is UpgradeDef => u !== undefined);
}

/** Upgrades of one archetype, in pool order. For the collection screen. */
export const byArchetype = (a: UpgradeArchetype): UpgradeDef[] =>
  UPGRADES.filter((u) => u.archetype === a);

// ── Describing the pool to a human ────────────────────────────────────
//
// Everything below this line is presentation. The sim never reads it,
// which is why it can be added without touching ENGINE_VERSION: no
// modifier, no rule and no draw is affected by a single byte of it.
//
// It exists because the draft was, for a while, completely unexplained.
// Twenty-eight cards were reachable in-game and none of them were
// described anywhere a player could look — you met a card once, mid
// fight, and then it was gone. A build system nobody can review is not
// a build system; it is four surprises.

export type ArchetypeInfo = {
  id: UpgradeArchetype;
  /** What the run turns into. Second person, because it is about you. */
  name: string;
  blurb: string;
};

export const ARCHETYPES: ArchetypeInfo[] = [
  {
    id: "aggression",
    name: "aggression",
    blurb:
      "Kill it faster and accept the bill. Bigger numbers on every verb you already own, usually paid for in HP, cooldown or PRIV.",
  },
  {
    id: "control",
    name: "control",
    blurb:
      "Decide when things happen. Longer freezes, slower patterns, cheaper interrupts — you are not out-damaging DAEMON, you are giving it fewer turns.",
  },
  {
    id: "greed",
    name: "greed",
    blurb:
      "Convert risk into economy. PRIV from grazing, grazing from proximity, proximity from nerve. Pays enormously and asks for the one thing you cannot buy back.",
  },
  {
    id: "survival",
    name: "survival",
    blurb:
      "Stay in the fight. More HP, longer mercy frames, softer hits. The archetype that turns a fast death into a slow win.",
  },
  {
    id: "chaos",
    name: "chaos",
    blurb:
      "Change the rules and find out. The cards that do something structural rather than something numeric. Rarely safe, occasionally absurd.",
  },
];

const ARCHETYPE_BY_ID: ReadonlyMap<UpgradeArchetype, ArchetypeInfo> = new Map(
  ARCHETYPES.map((a) => [a.id, a]),
);

export const archetypeInfo = (a: UpgradeArchetype): ArchetypeInfo =>
  ARCHETYPE_BY_ID.get(a) ?? ARCHETYPES[0]!;

/**
 * Which held cards are *actively* helping this one, right now.
 *
 * Distinct from `synergy`, which is a hint to the draft weighting and
 * merely says two cards belong together. This walks the real modifier
 * conditions and reports the `holds` links that are genuinely satisfied
 * — that is, the ones currently changing a number. The HUD uses it to
 * show that a build has come together, which was otherwise invisible:
 * the arithmetic happened, and the player had no way to see it.
 */
export function activeLinks(id: string, held: readonly string[]): string[] {
  const def = BY_ID.get(id);
  if (!def) return [];
  const out = new Set<string>();
  const walk = (c: Cond): void => {
    if (c.k === "holds") {
      if (held.includes(c.id)) out.add(c.id);
    } else if (c.k === "all") {
      c.of.forEach(walk);
    }
  };
  for (const m of def.mods) if (m.when) walk(m.when);
  return [...out];
}

/** Look one up for display. Null rather than a throw: a stale id from
 *  an old save should render as nothing, not take the screen down. */
export const upgradeById = (id: string): UpgradeDef | null => BY_ID.get(id) ?? null;

/** The pool grouped for the codex, in a deliberate reading order. */
export const poolByArchetype = (): { info: ArchetypeInfo; cards: UpgradeDef[] }[] =>
  ARCHETYPES.map((info) => ({ info, cards: byArchetype(info.id) }));

/**
 * A build, canonicalised for grouping.
 *
 * Sorted, so the same four cards drafted in any order produce the same
 * string — otherwise "which builds win" would be split across up to 24
 * permutations of the identical build and every one of them would look
 * too rare to judge.
 */
export const buildSignature = (ids: readonly string[]): string =>
  ids.length === 0 ? "none" : [...ids].sort().join("+");

/**
 * The coarse shape of a build: archetype counts, biggest first.
 *
 * `aggression3+chaos1` rather than four card ids. Low cardinality —
 * there are only a few dozen reachable shapes against tens of thousands
 * of hands — so it fills a dashboard usefully from day one, where the
 * full signature needs real traffic before any single row means
 * anything. It is also the question most tuning actually asks: not
 * "does this card win" but "does committing to one archetype win".
 */
export function buildArchetypes(ids: readonly string[]): string {
  if (ids.length === 0) return "none";
  const counts = new Map<string, number>();
  for (const id of ids) {
    const u = BY_ID.get(id);
    if (!u) continue;
    counts.set(u.archetype, (counts.get(u.archetype) ?? 0) + 1);
  }
  if (counts.size === 0) return "none";
  return [...counts.entries()]
    // Count descending, then name, so the string is stable rather than
    // dependent on draft order.
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([a, n]) => `${a}${n}`)
    .join("+");
}
